import React, { useState } from "react";
import { Trans } from "react-i18next";
import LoginSecurityCode from "@components/login_security_code";
import LoginEmail from "@components/login_email";
import GreenhouseLogo from "@images/greenhouse_logo.svg";
import ClearLogoHorizontal from "@images/clear_logo_horizontal.png";
import useTranslation from "@hooks/use_translation";
import Layout from "@components/layout";

import "./login.scss";
import { Body, Link } from "@/sprout/typography";

type Props = {
  jobBoardToken: string;
  createPath: string;
  submitCodePath: string;
  ghrUrl: string;
};

const Login = (props: Props) => {
  const { jobBoardToken, createPath, submitCodePath, ghrUrl } = props;
  const { t } = useTranslation("misc");

  const searchParams = new URLSearchParams(
    typeof window !== "undefined" ? window.location.search : "",
  );
  const isClearVerification = !!searchParams.get("identity_verification_id");
  const companyName = searchParams.get("company_name") || "";

  const [showCode, setShowCode] = useState(false);
  const [email, setEmail] = useState<string>("");

  const Cta = () => (!showCode ? <div>{t("login.cta")}</div> : null);

  return (
    <div className="login">
      <div className="login__left">
        <div className="login__leaf"></div>
      </div>
      <div className="login__right">
        <div className="login__content-wrapper">
          <div className="login__content">
            {isClearVerification && companyName ? (
              <>
                <div className="flex items-center justify-center mb-6 w-full">
                  <img src={GreenhouseLogo} alt="MyGreenhouse Logo" className="h-10 w-auto" />
                  <div className="h-10 w-[2px] bg-gray-800 mx-8" />
                  <img src={ClearLogoHorizontal} alt="CLEAR" className="h-6 w-auto" />
                </div>
                <div className="login__heading">{t("login_clear.title")}</div>
                <div className="login__subheader">
                  <div>{t("login_clear.description", { company_name: companyName })}</div>
                  <Cta />
                </div>
              </>
            ) : (
              <>
                <img src={GreenhouseLogo} alt="Greenhouse logo" height="40" />
                <div
                  className="login__heading"
                  dangerouslySetInnerHTML={{ __html: t("login.splash") }}
                />
                <div className="login__subheader">
                  <div>{t("login.subheader")}</div>
                  <Cta />
                </div>
              </>
            )}

            {showCode ? (
              <LoginSecurityCode
                submitCodePath={submitCodePath}
                email={email}
                setShowCode={setShowCode}
              />
            ) : (
              <LoginEmail
                jobBoardToken={jobBoardToken}
                createPath={createPath}
                setShowCode={setShowCode}
                email={email}
                setEmail={setEmail}
              />
            )}

            <div className="login__agreement">
              <Body secondary>
                <Trans
                  i18nKey="misc:login:agreement"
                  components={{
                    PrivacyPolicy: (
                      <Link secondary external href={t("newUser.privacy_policy.url")}>
                        {t("newUser.privacy_policy.text")}
                      </Link>
                    ),
                    UserAgreement: (
                      <Link secondary external href={t("newUser.user_agreement.url")}>
                        {t("newUser.user_agreement.text")}
                      </Link>
                    ),
                  }}
                />
              </Body>
            </div>

            <div className="login__footer">
              <Body secondary>
                {t("login.ghr")}{" "}
                <Link secondary external href={ghrUrl}>
                  {t("login.signIn")}
                </Link>
              </Body>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

Login.layout = (page: React.ReactNode) => <Layout publicFacing>{page}</Layout>;

export default Login;
